/**
 * Безопасность учётной записи: пароль и второй фактор.
 *
 * Коды восстановления показываются один раз — при подключении. Это единственный
 * момент, когда их можно записать, и об этом сказано прямо: телефон теряют,
 * и без кодов вход придётся восстанавливать администратору руками.
 */

import * as React from "react";
import { Check, Clock, KeyRound, ShieldCheck, ShieldOff } from "lucide-react";

import {
  changePassword,
  confirmTotp,
  disableTotp,
  getSessionLifetime,
  setSessionLifetime,
  setupTotp,
  type CurrentUser,
  type TotpSetup,
} from "@/shared/api/auth";
import { ApiError } from "@/shared/api/client";
import { useSession } from "@/shared/lib/session";
import { Button } from "@/shared/ui/Button";
import { InlineAlert } from "@/shared/ui/Feedback";
import { Dialog } from "@/shared/ui/Overlays";
import { Select } from "@/shared/ui/Select";
import { TextField } from "@/shared/ui/TextField";
import { useToast } from "@/shared/ui/Toast";

export interface SecurityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: CurrentUser;
}

type Step = "overview" | "setup" | "disable" | "password";

/** Пустая строка — «как в контуре», «0» — не истекает. */
const LIFETIMES = [
  { value: "", label: "Как в системе" },
  { value: "3600", label: "1 час" },
  { value: "28800", label: "8 часов" },
  { value: "86400", label: "Сутки" },
  { value: "604800", label: "Неделя" },
  { value: "2592000", label: "30 дней" },
  { value: "0", label: "Не истекает" },
];
const ENDLESS = "0";

/** Где на самом деле меняют пароль, если вход не местный. */
const DIRECTORY_HINT: Record<string, string> = {
  ldap: "Пароль живёт в каталоге предприятия — меняйте его там",
  oidc: "Пароль живёт у поставщика входа — меняйте его там",
};

export function SecurityDialog({
  open,
  onOpenChange,
  user,
}: SecurityDialogProps): React.ReactElement {
  const toast = useToast();
  const restore = useSession((state) => state.restore);
  const [step, setStep] = React.useState<Step>("overview");
  // «Как в контуре» — пустая строка: у Select это значение по умолчанию
  const [lifetime, setLifetime] = React.useState("");
  const [setup, setSetup] = React.useState<TotpSetup | null>(null);
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setStep("overview");
      setSetup(null);
      setCode("");
      setPassword("");
      setNewPassword("");
      setError(null);
    }
  }, [open]);

  function fail(cause: unknown, fallback: string): void {
    setError(cause instanceof ApiError ? cause.message : fallback);
  }

  async function startSetup(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setSetup(await setupTotp());
      setStep("setup");
    } catch (cause) {
      fail(cause, "Не удалось получить секрет");
    } finally {
      setBusy(false);
    }
  }

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await confirmTotp(code);
      await restore();
      toast.show({ title: "Двухфакторная аутентификация включена", tone: "success" });
      onOpenChange(false);
    } catch (cause) {
      fail(cause, "Не удалось подтвердить код");
    } finally {
      setBusy(false);
    }
  }

  async function turnOff(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await disableTotp(password);
      await restore();
      toast.show({ title: "Двухфакторная аутентификация отключена" });
      onOpenChange(false);
    } catch (cause) {
      fail(cause, "Не удалось отключить второй фактор");
    } finally {
      setBusy(false);
    }
  }

  // Срок читается при открытии: он живёт в профиле, а не в текущей сессии
  React.useEffect(() => {
    if (!open) {
      return;
    }
    let alive = true;
    void getSessionLifetime()
      .then((value) => {
        if (alive) {
          setLifetime(value.seconds === null ? "" : String(value.seconds));
        }
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [open]);

  async function chooseLifetime(value: string): Promise<void> {
    const previous = lifetime;
    setLifetime(value);
    try {
      await setSessionLifetime(value === "" ? null : Number(value));
      toast.show({ title: "Срок сессии изменён", description: "Подействует со следующего входа" });
    } catch (cause) {
      setLifetime(previous);
      fail(cause, "Не удалось изменить срок сессии");
    }
  }

  async function savePassword(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await changePassword(password, newPassword);
      toast.show({ title: "Пароль изменён", tone: "success" });
      onOpenChange(false);
    } catch (cause) {
      fail(cause, "Не удалось сменить пароль");
    } finally {
      setBusy(false);
    }
  }

  const titles: Record<Step, string> = {
    overview: "Безопасность",
    setup: "Подключение двухфакторной аутентификации",
    disable: "Отключить второй фактор",
    password: "Смена пароля",
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={titles[step]}
      width={step === "setup" ? 560 : 480}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Закрыть</Button>
          {step === "setup" && (
            <Button variant="primary" loading={busy} onClick={() => void confirm()}>
              Подтвердить код
            </Button>
          )}
          {step === "disable" && (
            <Button variant="danger" loading={busy} onClick={() => void turnOff()}>
              Отключить
            </Button>
          )}
          {step === "password" && (
            <Button variant="primary" loading={busy} onClick={() => void savePassword()}>
              Сменить пароль
            </Button>
          )}
        </>
      }
    >
      {error && (
        <InlineAlert tone="danger" className="mb-[var(--pt-space-12)]">
          {error}
        </InlineAlert>
      )}

      {step === "overview" && (
        <div className="flex flex-col gap-[var(--pt-space-12)]">
          <div className="flex items-start gap-[var(--pt-space-8)]">
            {user.totp_enabled ? (
              <ShieldCheck className="mt-[2px] size-[var(--pt-icon-lg)] text-[var(--pt-success-text)]" />
            ) : (
              <ShieldOff className="mt-[2px] size-[var(--pt-icon-lg)] text-[var(--pt-text-tertiary)]" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[var(--pt-font-ui)] font-medium">
                {user.totp_enabled
                  ? "Двухфакторная аутентификация включена"
                  : "Двухфакторная аутентификация выключена"}
              </p>
              <p className="mt-[var(--pt-space-2)] text-[var(--pt-fs-xs)] text-[var(--pt-text-secondary)]">
                Второй фактор защищает вход, даже если пароль стал известен посторонним.
              </p>
            </div>
            {user.totp_enabled ? (
              <Button size="sm" variant="dangerGhost" onClick={() => setStep("disable")}>
                Отключить
              </Button>
            ) : (
              <Button size="sm" variant="primary" loading={busy} onClick={() => void startSetup()}>
                Подключить
              </Button>
            )}
          </div>

          <div className="flex items-start gap-[var(--pt-space-8)] border-t border-[var(--pt-border-subtle)] pt-[var(--pt-space-12)]">
            <Clock className="mt-[2px] size-[var(--pt-icon-lg)] text-[var(--pt-text-tertiary)]" />
            <div className="min-w-0 flex-1">
              <p className="text-[var(--pt-font-ui)] font-medium">Срок сессии</p>
              <p className="mt-[var(--pt-space-2)] text-[var(--pt-fs-xs)] text-[var(--pt-text-secondary)]">
                Через это время без работы придётся войти заново. За общим компьютером стоит выбрать
                поменьше.
              </p>
              {lifetime === ENDLESS && (
                <p className="mt-[var(--pt-space-4)] text-[var(--pt-fs-xs)] text-[var(--pt-warning-text)]">
                  Бессрочная сессия останется открытой и на чужом устройстве. Закрыть её можно будет
                  только сменой пароля.
                </p>
              )}
            </div>
            <div className="w-[190px] shrink-0">
              <Select
                ariaLabel="Срок сессии"
                value={lifetime}
                onValueChange={(value) => void chooseLifetime(value)}
                options={LIFETIMES}
              />
            </div>
          </div>

          <div className="flex items-start gap-[var(--pt-space-8)] border-t border-[var(--pt-border-subtle)] pt-[var(--pt-space-12)]">
            <KeyRound className="mt-[2px] size-[var(--pt-icon-lg)] text-[var(--pt-text-tertiary)]" />
            <div className="min-w-0 flex-1">
              <p className="text-[var(--pt-font-ui)] font-medium">Пароль</p>
              <p className="mt-[var(--pt-space-2)] text-[var(--pt-fs-xs)] text-[var(--pt-text-secondary)]">
                {user.auth_provider === "local"
                  ? user.email
                  : (DIRECTORY_HINT[user.auth_provider] ?? "Пароль хранится не в PlanTask")}
              </p>
            </div>
            {/* Пароль пользователя каталога живёт в каталоге: форма здесь
                ничего бы не изменила, а человек ушёл бы в уверенности, что
                сменил его */}
            {user.auth_provider === "local" && (
              <Button size="sm" onClick={() => setStep("password")}>
                Сменить
              </Button>
            )}
          </div>
        </div>
      )}

      {step === "setup" && setup && (
        <div className="flex flex-col gap-[var(--pt-space-12)]">
          <p className="text-[var(--pt-fs-sm)] text-[var(--pt-text-secondary)]">
            Отсканируйте код в приложении-аутентификаторе и введите шесть цифр, которые оно покажет.
          </p>
          <div className="flex gap-[var(--pt-space-16)]">
            <div
              className="size-[160px] shrink-0 rounded-[var(--pt-radius-3)] bg-white p-[var(--pt-space-8)] [&_svg]:size-full"
              // Код приходит с сервера готовым SVG: это наш собственный ответ,
              // а не пользовательский ввод
              dangerouslySetInnerHTML={{ __html: setup.qr_code_svg }}
            />
            <div className="min-w-0 flex-1">
              <p className="text-[var(--pt-fs-xs)] text-[var(--pt-text-secondary)]">
                Если сканировать нечем, введите ключ вручную:
              </p>
              <code className="mt-[var(--pt-space-4)] block break-all font-[family-name:var(--pt-font-mono)] text-[var(--pt-fs-xs)]">
                {setup.secret}
              </code>
              <TextField
                label="Код из приложения"
                inputMode="numeric"
                autoFocus
                value={code}
                onChange={(event) => setCode(event.target.value)}
                containerClassName="mt-[var(--pt-space-8)]"
              />
            </div>
          </div>

          <InlineAlert tone="warning" title="Сохраните коды восстановления">
            Их показывают один раз. Каждый работает вместо кода из приложения — по одному разу.
            <div className="mt-[var(--pt-space-8)] grid grid-cols-2 gap-x-[var(--pt-space-16)] gap-y-[var(--pt-space-2)] font-[family-name:var(--pt-font-mono)] text-[var(--pt-fs-xs)]">
              {setup.recovery_codes.map((recovery) => (
                <span key={recovery}>{recovery}</span>
              ))}
            </div>
          </InlineAlert>
        </div>
      )}

      {step === "disable" && (
        <div className="flex flex-col gap-[var(--pt-space-12)]">
          <p className="text-[var(--pt-fs-sm)] text-[var(--pt-text-secondary)]">
            Подтвердите паролем: иначе украденная сессия сможет снять защиту.
          </p>
          <TextField
            label="Пароль"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
      )}

      {step === "password" && (
        <div className="flex flex-col gap-[var(--pt-space-8)]">
          <TextField
            label="Текущий пароль"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <TextField
            label="Новый пароль"
            type="password"
            autoComplete="new-password"
            hint="Не короче десяти символов"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          {newPassword.length > 0 && newPassword.length < 10 && (
            <p className="text-[var(--pt-fs-xs)] text-[var(--pt-text-tertiary)]">
              <Check className="mr-[var(--pt-space-2)] inline size-[12px]" />
              осталось символов: {10 - newPassword.length}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
